import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { Search } from 'lucide-react';
import { PORTAL_API } from './constants';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';

export function SupplierItemsTab({ items, itemSearch, setItemSearch, onReload, token, categories: initialCategories }) {
  const [priceModal, setPriceModal] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [priceReason, setPriceReason] = useState('');
  const [priceMode, setPriceMode] = useState('absolute');
  const [pricePct, setPricePct] = useState('');
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const [bulkPct, setBulkPct] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [categories, setCategories] = useState(initialCategories || []);
  const [sortState, handleSort] = useSortState('item_code', 'asc');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const handlePriceRequest = async () => {
    if (!priceModal || !newPrice) return;
    try {
      await api.post(`${PORTAL_API}/my-items/${priceModal}/price-request`, { new_price: newPrice, reason: priceReason }, authHeaders);
      toast.success('Price change request submitted');
      setPriceModal(null); setNewPrice(''); setPriceReason('');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  if (priceModal) {
    const item = items.find(i => i.item_code === priceModal) || {};
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Edit Item File</h2>
          <button onClick={() => setPriceModal(null)} className="text-slate-400 hover:text-slate-600 text-xl" aria-label="Close">&times;</button>
        </div>
        <div className="border rounded-lg bg-white p-6">
          <div className="grid grid-cols-3 gap-6">
            {/* Column 1 */}
            <div className="space-y-3">
              <div><label className="text-xs font-semibold text-red-600">Item Code</label><input value={item.item_code || ''} disabled className="w-full border rounded p-2 text-sm bg-slate-50" /></div>
              <div><label className="text-xs font-semibold text-red-600">Category Code</label><input value={item.category_code || ''} disabled className="w-full border rounded p-2 text-sm bg-slate-50" /></div>
              <div><label className="text-xs font-semibold text-red-600">Description</label><textarea value={item.description || item.outline || ''} disabled className="w-full border rounded p-2 text-sm bg-slate-50 h-20" /></div>
              <div><label className="text-xs font-semibold">UPC</label><input value={item.upc || ''} disabled className="w-full border rounded p-2 text-sm bg-slate-50" /></div>
              <div><label className="text-xs font-semibold">Purchase Unit</label><input value={item.uom || item.purchase_unit || 'EACH'} disabled className="w-full border rounded p-2 text-sm bg-slate-50" /></div>
              <div><label className="text-xs font-semibold">Discount %</label><input value={item.discount || '0.00'} disabled className="w-full border rounded p-2 text-sm bg-slate-50" /></div>
              <div>
                <label className="text-xs font-semibold">Price (USD)</label>
                <input value={item.price != null ? Number(item.price).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '0.00'} disabled className="w-full border rounded p-2 text-sm bg-slate-50 font-mono" />
              </div>
              <div className="flex items-center gap-3">
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => {
                  setNewPrice(item.price || '');
                  setPriceReason('');
                }}>New Price</Button>
              </div>
              {newPrice !== '' && (
                <div className="border rounded p-3 bg-emerald-50 space-y-2">
                  <div className="flex gap-2">
                    <button onClick={() => setPriceMode('absolute')} className={`px-3 py-1 text-xs rounded ${priceMode === 'absolute' ? 'bg-emerald-600 text-white' : 'border'}`}>Absolute</button>
                    <button onClick={() => setPriceMode('percentage')} className={`px-3 py-1 text-xs rounded ${priceMode === 'percentage' ? 'bg-emerald-600 text-white' : 'border'}`}>Percentage</button>
                  </div>
                  {priceMode === 'absolute' ? (
                    <div><label className="text-xs font-medium">New Price ($)</label><input type="number" value={newPrice} onChange={e => setNewPrice(e.target.value)} className="w-full border rounded p-2 text-sm" /></div>
                  ) : (
                    <div>
                      <label className="text-xs font-medium">% Change</label>
                      <input type="number" value={pricePct} onChange={e => { setPricePct(e.target.value); const p = parseFloat(item.price || 0); setNewPrice(String(Math.round(p * (1 + parseFloat(e.target.value || 0) / 100) * 100) / 100)); }} className="w-full border rounded p-2 text-sm" placeholder="e.g. 5 for +5%" />
                      <p className="text-xs text-slate-500 mt-1">New price: ${newPrice}</p>
                    </div>
                  )}
                  <div><label className="text-xs font-medium">Reason</label><input value={priceReason} onChange={e => setPriceReason(e.target.value)} className="w-full border rounded p-2 text-sm" placeholder="Reason for price change" /></div>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handlePriceRequest}>Submit New Price</Button>
                </div>
              )}
            </div>
            {/* Column 2 */}
            <div className="space-y-3">
              <div><label className="text-xs font-semibold text-red-600">Outline</label><input value={item.outline || ''} disabled className="w-full border rounded p-2 text-sm bg-slate-50" /></div>
              <div><label className="text-xs font-semibold text-red-600">Item Type</label><input value={item.item_type || 'Consumable'} disabled className="w-full border rounded p-2 text-sm bg-slate-50" /></div>
              <div><label className="text-xs font-semibold">Suppliers Part Number</label><input value={item.supplier_part_no || ''} disabled className="w-full border rounded p-2 text-sm bg-slate-50" /></div>
              <div><label className="text-xs font-semibold">Lead Time</label><input value={item.lead_time || '0'} disabled className="w-full border rounded p-2 text-sm bg-slate-50" /></div>
              <div><label className="text-xs font-semibold">Sales Tax %</label><input value={item.sales_tax || '0.00'} disabled className="w-full border rounded p-2 text-sm bg-slate-50" /></div>
            </div>
            {/* Column 3 */}
            <div className="space-y-3">
              <div><label className="text-xs font-semibold">Add Image</label>
                <div className="border-2 border-dashed rounded p-4 text-center text-xs text-slate-400 h-32 flex items-center justify-center">
                  {item.image_url ? <img src={item.image_url} alt="" className="max-h-28 object-contain" /> : 'Drag & drop file here.'}
                </div>
              </div>
              <div><label className="text-xs font-semibold">Pack Unit</label><div className="flex items-center gap-2"><span className="text-sm">EACH =</span><input value="1" disabled className="w-16 border rounded p-2 text-sm bg-slate-50" /></div></div>
              <div><label className="text-xs font-semibold">Sub Pack Unit</label><div className="flex items-center gap-2"><span className="text-sm">Kilogram =</span><input value="1" disabled className="w-16 border rounded p-2 text-sm bg-slate-50" /></div></div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={item.active} disabled className="h-4 w-4" /> Active</label>
            <Button variant="outline" size="sm" onClick={() => setPriceModal(null)}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Items</h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={async () => {
            try { const r = await api.get(`${PORTAL_API}/my-items/categories`, authHeaders); setCategories(r.data.data || []); } catch {}
            setBulkPriceOpen(true);
          }}>Bulk Price Update</Button>
          <span className="text-xs text-slate-500">Total: {items.length}</span>
        </div>
      </div>
      {/* Bulk Price Update Modal */}
      {bulkPriceOpen && (
        <div className="border rounded-lg bg-amber-50 p-4 mb-4">
          <h3 className="font-bold text-sm mb-2">Bulk Price Update</h3>
          <p className="text-xs text-slate-500 mb-3">Apply a percentage increase or decrease to all items, or to a specific category.</p>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs font-medium">% Change</label><input type="number" value={bulkPct} onChange={e => setBulkPct(e.target.value)} className="w-full border rounded p-2 text-sm" placeholder="e.g. 5 or -3" /></div>
            <div><label className="text-xs font-medium">Category (blank = all)</label>
              <select value={bulkCategory} onChange={e => setBulkCategory(e.target.value)} className="w-full border rounded p-2 text-sm">
                <option value="">All Items</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Reason</label><input value={bulkReason} onChange={e => setBulkReason(e.target.value)} className="w-full border rounded p-2 text-sm" placeholder="Optional" /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={async () => {
              if (!bulkPct) { toast.error('Enter a percentage'); return; }
              try {
                const r = await api.post(`${PORTAL_API}/my-items/bulk-price-update`, { percentage: parseFloat(bulkPct), category: bulkCategory, reason: bulkReason }, authHeaders);
                toast.success(r.data.message);
                setBulkPriceOpen(false); setBulkPct(''); setBulkCategory(''); setBulkReason('');
              } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
            }}>Submit Bulk Update</Button>
            <Button size="sm" variant="outline" onClick={() => setBulkPriceOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}
      <div className="relative mb-3 max-w-xs">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
        <input value={itemSearch} onChange={e => setItemSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && onReload()}
          className="pl-9 h-9 text-sm border rounded w-full" placeholder="Search items..." />
      </div>
      <div className="border rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b">
            <SortableTableHeader label="Item Code" field="item_code" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Outline" field="description" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Supplier Part No." field="supplier_part_number" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Active" field="active" sortState={sortState} onSort={handleSort} className="text-center" />
          </tr></thead>
          <tbody>
            {sortData(items, sortState.field, sortState.dir).map(i => (
              <tr key={i.item_code} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => { setPriceModal(i.item_code); setNewPrice(''); setPriceReason(''); }}>
                <td className="px-3 py-2 font-mono text-xs">{i.item_code}</td>
                <td className="px-3 py-2">{i.outline}</td>
                <td className="px-3 py-2 text-xs">{i.supplier_part_no || ''}</td>
                <td className="px-3 py-2 text-center text-xs">{i.active ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
